/*
 * const {
 *   phase,       // 'idle' | 'review' | 'processing' | 'complete'
 *   rows,        // array rendered by the <SimpleTable>
 *   totals,      // { succeeded, failed, amount, sms, email }
 *   progress,    // 0 → 1 (fraction of rows processed)
 *   initialise,  // (preparedRows, strategy)   → sets rows and pre‑flight status
 *   runBatch,    // async ({ strategy, meta, user })
 *   reset,       // resets to initial 'idle' state
 * } = useBatchWorkflow();
 */

import { useReducer, useCallback } from "react";
import { sendBatch, evaluateStatus } from "../utils";


// -------------------------- State -------------------------------------------

const initialState = {
  phase: "idle", // 'idle' | 'review' | 'processing' | 'complete'
  rows: [],
  totals: { succeeded: 0, failed: 0, amount: 0, sms: 0, email: 0 },
  progress: 0, // 0 .. 1
};

function reducer(state, action) {
  switch (action.type) {
    case "setRows":
      return { ...state, rows: action.rows };
    case "setPhase":
      return { ...state, phase: action.phase };
    case "setTotals":
      return { ...state, totals: action.totals };
    case "setProgress":
      return { ...state, progress: action.progress };
    case "reset":
      return initialState;
    default:
      return state;
  }
}

// ------------------------ Hook ---------------------------------------------

export const useBatchWorkflow = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const initialiseRowsWithSendStatus = useCallback((preparedRows, strategy) => {
    const rowsWithStatus = preparedRows.map((r) => ({
      ...r,
      status: evaluateStatus(r, strategy),
    }));
    dispatch({ type: "setRows", rows: rowsWithStatus });
    dispatch({ type: "setPhase", phase: "review" });
  }, []);

  const runBatch = useCallback(
    async ({ strategy, meta, user, paymentSettings, rowsOverride }) => {
      dispatch({ type: "setPhase", phase: "processing" });

      const { updatedRows, totals } = await sendBatch({
        rows:
          rowsOverride && Array.isArray(rowsOverride)
            ? rowsOverride
            : state.rows,
        strategy,
        meta,
        user,
        paymentSettings,
        onProgress: (done, total) => {
          dispatch({ type: "setProgress", progress: done / total });
        },
      });

      dispatch({ type: "setRows", rows: updatedRows });
      dispatch({ type: "setTotals", totals });
      dispatch({ type: "setPhase", phase: "complete" });
    },
    [state.rows],
  );

  const resetState = useCallback(() => dispatch({ type: "reset" }), []);

  return {
    phase: state.phase,
    rows: state.rows,
    totals: state.totals,
    progress: state.progress,
    initialiseRowsWithSendStatus,
    runBatch,
    resetState,
  };
};
